import type { TFunction } from '@mukil/i18n';
import type {
  LeaveActionFailure,
  LeaveCancelFailure,
  LeaveRequestFailure,
  LeaveState,
} from '../../data/use_leave_requests';
import type { LeaveRequest, LeaveStatus } from '../../data/wagepro_store_leave';

/** Queue order shown in the leave tab strip. */
export const LEAVE_STATUSES: readonly LeaveStatus[] = [
  'pending',
  'approved',
  'rejected',
  'cancelled',
] as const;

/** Only a pending request can still be approved or rejected. */
export function is_actionable(request: LeaveRequest): boolean {
  return request.status === 'pending';
}

/** A pending or approved request can be cancelled. */
export function is_cancellable(request: LeaveRequest): boolean {
  return request.status === 'pending' || request.status === 'approved';
}

/**
 * What the Leave screen should render for a given `LeaveState` + filtered
 * row count. Pure and independently-tested — mirrors
 * `resolve_expenses_view`'s state-precedence rules exactly:
 * `loading`/`no_entity`/`entitlement_required`/`error` MUST all be checked
 * before the `ready`-with-zero-filtered-rows branch.
 */
export type LeaveView =
  | 'loading'
  | 'no_entity'
  | 'entitlement_required'
  | 'error'
  | 'empty'
  | 'rows';

export function resolve_leave_view(input: {
  state: LeaveState;
  filtered_row_count: number;
}): LeaveView {
  if (input.state.status === 'loading') return 'loading';
  if (input.state.status === 'no_entity') return 'no_entity';
  if (input.state.status === 'entitlement_required') {
    return 'entitlement_required';
  }
  if (input.state.status === 'error') return 'error';
  return input.filtered_row_count === 0 ? 'empty' : 'rows';
}

/**
 * A human-readable explanation of an `approveLeave`/`rejectLeave` failure —
 * never a raw error toast. See `use_leave_requests.ts`'s `LeaveActionFailure`
 * docblock for what each case means.
 *
 * Takes `t` as a parameter (rather than the caller passing the RETURNED KEY
 * to its own `t(...)`) so every branch's literal `t('leave.action_error_*')`
 * call is visible to the static i18n key extractor (`PARAM_T_RE`,
 * `scripts/lib/i18n_extract.ts`) — a function that returns a key STRING for
 * the caller to translate produces a call site shaped `t(some_function(...))`,
 * a DYNAMIC argument the extractor cannot resolve, so every key it alone
 * referenced was misreported as an orphan.
 */
export function leave_action_error_message(
  t: TFunction,
  failure: LeaveActionFailure,
): string {
  switch (failure) {
    case 'entitlement_required':
      return t('leave.action_error_entitlement_required');
    case 'forbidden':
      return t('leave.action_error_forbidden');
    case 'not_found':
      return t('leave.action_error_not_found');
    case 'already_decided':
      return t('leave.action_error_already_decided');
    case 'insufficient_balance':
      return t('leave.action_error_insufficient_balance');
    case 'stale_balance':
      return t('leave.action_error_stale_balance');
    case 'contractor_not_eligible':
      return t('errors.contractor_not_eligible');
    case 'error':
      return t('leave.action_error_generic');
  }
}

/** A human-readable explanation of a `cancelLeave` failure. See {@link leave_action_error_message}'s docblock for why `t` is a parameter. */
export function leave_cancel_error_message(
  t: TFunction,
  failure: LeaveCancelFailure,
): string {
  switch (failure) {
    case 'entitlement_required':
      return t('leave.cancel_error_entitlement_required');
    case 'forbidden':
      return t('leave.cancel_error_forbidden');
    case 'not_found':
      return t('leave.cancel_error_not_found');
    case 'already_terminal':
      return t('leave.cancel_error_already_terminal');
    case 'pay_run_overlap':
      return t('leave.cancel_error_pay_run_overlap');
    case 'error':
      return t('leave.cancel_error_generic');
  }
}

/** A human-readable explanation of a `requestLeave` failure from the "Record leave" drawer. See {@link leave_action_error_message}'s docblock for why `t` is a parameter. */
export function leave_record_error_message(
  t: TFunction,
  failure: LeaveRequestFailure,
): string {
  switch (failure) {
    case 'entitlement_required':
      return t('leave.record_error_entitlement_required');
    case 'forbidden':
      return t('leave.record_error_forbidden');
    case 'not_found':
      return t('leave.record_error_not_found');
    case 'overlap':
      return t('leave.record_error_overlap');
    case 'invalid_dates':
      return t('leave.record_error_invalid_dates');
    case 'unknown_leave_type':
      return t('leave.record_error_unknown_leave_type');
    case 'contractor_not_eligible':
      return t('errors.contractor_not_eligible');
    case 'error':
      return t('leave.record_error_generic');
  }
}

/**
 * Derives the number of business days between two ISO date strings (inclusive).
 * Used by the record-leave drawer to auto-calculate `days_requested` before
 * the user overrides it. Pure function — unit-testable with no DOM.
 *
 * Counts Mon–Fri; does not subtract public holidays (same stance as Xero's
 * "working days" helper). Returns 0 when `end < start`.
 */
export function count_weekdays(start_iso: string, end_iso: string): number {
  const start = new Date(start_iso);
  const end = new Date(end_iso);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return 0;
  if (end < start) return 0;
  let count = 0;
  const cursor = new Date(start);
  while (cursor <= end) {
    const day = cursor.getUTCDay();
    if (day !== 0 && day !== 6) count += 1;
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return count;
}
